import type { ReactNode } from 'react'
import { ModuleLayout } from '@/components/layout/ModuleLayout'
import { cn } from '@/lib/utils'

import { PermissionEmptyState } from './PermissionEmptyState'

interface AccessStateProps {
  /** Optional escape hatch, e.g. "Back to Projects" on record routes. */
  backLabel?: string
  onBack?: () => void
  className?: string
}

/**
 * The one access-denied body (design audit S19): the locked empty visual
 * centred in the content area. ModuleAccessState pairs it with a title-only
 * layout that also drops the auto "..." pin menu, so a page the viewer
 * cannot read offers nothing to pin.
 */
export function AccessState({
  backLabel,
  onBack,
  className,
}: AccessStateProps) {
  return (
    <div
      className={cn('flex h-full items-center justify-center p-6', className)}
    >
      <PermissionEmptyState actionLabel={backLabel} onAction={onBack} />
    </div>
  )
}

interface ModuleAccessStateProps extends AccessStateProps {
  moduleSlug: string
  /** The module name, rendered as the 20 px condensed page title. */
  title: string
  /** Record routes pass their id so the layout keeps its detail chrome. */
  entityId?: string
  entityParamName?: string
  isDetailView?: boolean
  children?: ReactNode
}

/**
 * A whole denied page: module title only (condensed, no tabs, no actions,
 * no description) above the AccessState body.
 */
export function ModuleAccessState({
  moduleSlug,
  title,
  entityId,
  entityParamName,
  isDetailView = false,
  backLabel,
  onBack,
  children,
}: ModuleAccessStateProps) {
  return (
    <ModuleLayout
      moduleSlug={moduleSlug}
      title={title}
      description={null}
      actions={null}
      tabs={[]}
      hidePinMenu
      entityId={entityId}
      entityParamName={entityParamName}
      isDetailView={isDetailView}
      fullWidth={false}
      noPadding={true}
      fullHeight={true}
      condensed={true}
    >
      {children}
      <AccessState backLabel={backLabel} onBack={onBack} />
    </ModuleLayout>
  )
}
